import type { ChangeEvent, FocusEventHandler } from "react";

import { type Control, Controller } from "react-hook-form";

import { Input } from "@alpacahq/ui/old";

import { ToggleOptions } from "~/v2/components/common/ToggleOptions";
import type { QuantityType } from "~/v2/constants/equityOrder";
import type { EquityOrderTicketSchema } from "../schema";

const QUANTITY_TYPE_OPTIONS: { label: string; value: QuantityType }[] = [
  {
    label: "#",
    value: "shares",
  },
  {
    label: "$",
    value: "dollars",
  },
];

interface QuantityInputProps {
  control: Control<EquityOrderTicketSchema>;
  isQuantityTypeDisabled: boolean;
  quantityType: QuantityType;
  onQuantityChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onQuantityBlur: FocusEventHandler<HTMLInputElement>;
  onQuantityTypeChange: (quantityType: QuantityType) => void;
  sharesAvailable: number | undefined;
  dollarsAvailable: string | undefined;
}

export const QuantityInput = ({
  control,
  isQuantityTypeDisabled,
  quantityType,
  onQuantityTypeChange,
  onQuantityChange,
  onQuantityBlur,
  sharesAvailable,
  dollarsAvailable,
}: QuantityInputProps) => {
  const available =
    quantityType === "shares" ? sharesAvailable : dollarsAvailable;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-1">
        <label
          htmlFor="quantity"
          className="font-semibold text-gray-700 text-sm"
        >
          {quantityType === "shares" ? "Quantity" : "Amount"}
        </label>
        <div className="flex items-center justify-end gap-1">
          {available && (
            <p className="m-0 p-0 text-right text-gray-800 text-xs">
              <span className="truncate font-mono">{available}</span> available
            </p>
          )}
          {!isQuantityTypeDisabled && (
            <ToggleOptions
              options={QUANTITY_TYPE_OPTIONS}
              value={quantityType}
              onChange={onQuantityTypeChange}
              size="xs"
            />
          )}
        </div>
      </div>
      <Controller
        name="quantity"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Input
            id="quantity"
            type="number"
            min="0"
            step="any"
            {...field}
            onChange={(event) => {
              onQuantityChange(event);
              field.onChange(event);
            }}
            onBlur={(event) => {
              field.onBlur();
              onQuantityBlur(event);
            }}
            errorMsg={error?.message}
          />
        )}
      />
    </div>
  );
};
